import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { UserApiService } from '../../shared/services/api/user-api.service';
import {
  FeatureFlagService,
  WEEKLY_DIGEST_ENABLED_FLAG_KEY,
} from '../feature-flags/feature-flag.service';

/**
 * Route guard for the digest-audience admin surface (`/admin/email-subscriptions`).
 *
 * Fails closed while the weekly digest rollout is off for the caller, then activates only
 * for holders of `email_subscriptions.manage` (read from `GET /user/auth/me`). Everyone
 * else is bounced to `/home`.
 */
export const emailSubscriptionsAdminGuard: CanActivateFn = async () => {
  const userApi = inject(UserApiService);
  const router = inject(Router);
  const flags = inject(FeatureFlagService);

  await flags.ready();
  if (
    !flags.isEnabledWithFallback(
      WEEKLY_DIGEST_ENABLED_FLAG_KEY,
      environment.features.weeklyDigestEnabled
    )
  ) {
    return router.createUrlTree(['/home']);
  }

  try {
    const me = await firstValueFrom(userApi.getUserData());
    if (me.permissions?.includes('email_subscriptions.manage')) {
      return true;
    }
  } catch {
    // Network / auth issues fall through to redirect — the authTokenInterceptor
    // already handles the 401/403 cases at the HTTP layer.
  }
  return router.createUrlTree(['/home']);
};
